import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface Accent {
  name: string;
  from: string;
  to: string;
}

interface FontSize {
  name: string;
  scale: number;
}

interface Spacing {
  name: string;
  scale: number;
}

interface ThemeState {
  // Theme Mode
  isDark: boolean;
  
  // Colors & Effects
  accent: Accent;
  glassOpacity: number;
  borderOpacity: number;
  
  // Typography
  fontSize: FontSize;
  monoFont: boolean;
  
  // Layout & Spacing
  spacing: Spacing;
  compactMode: boolean;
  
  // Animation & Effects
  enableAnimations: boolean;
  reduceMotion: boolean;
  enableBlur: boolean;
  
  // Card Style
  cardStyle: 'glass' | 'solid' | 'minimal';
  roundedCorners: 'none' | 'small' | 'medium' | 'large' | 'full';
  
  // Actions
  setTheme: (isDark: boolean) => void;
  setAccent: (accent: Accent) => void;
  setGlassOpacity: (opacity: number) => void;
  setBorderOpacity: (opacity: number) => void;
  setFontSize: (size: FontSize) => void;
  setMonoFont: (enabled: boolean) => void;
  setSpacing: (spacing: Spacing) => void;
  setCompactMode: (enabled: boolean) => void;
  setEnableAnimations: (enabled: boolean) => void;
  setReduceMotion: (enabled: boolean) => void;
  setEnableBlur: (enabled: boolean) => void;
  setCardStyle: (style: 'glass' | 'solid' | 'minimal') => void;
  setRoundedCorners: (radius: 'none' | 'small' | 'medium' | 'large' | 'full') => void;
}

export const ACCENTS = {
  cyan: { name: 'Cyan', from: 'from-cyan-600', to: 'to-blue-600' },
  purple: { name: 'Purple', from: 'from-indigo-600', to: 'to-violet-600' },
  rose: { name: 'Rose', from: 'from-rose-600', to: 'to-pink-600' },
  amber: { name: 'Amber', from: 'from-amber-600', to: 'to-orange-600' },
  emerald: { name: 'Emerald', from: 'from-emerald-600', to: 'to-teal-600' },
} as const;

export const FONT_SIZES = {
  small: { name: 'Small', scale: 0.875 },
  medium: { name: 'Medium', scale: 1 },
  large: { name: 'Large', scale: 1.125 },
} as const;

export const SPACINGS = {
  compact: { name: 'Compact', scale: 0.875 },
  comfortable: { name: 'Comfortable', scale: 1 },
  relaxed: { name: 'Relaxed', scale: 1.25 },
} as const;

export const AVAILABLE_ACCENTS = Object.values(ACCENTS);
export const AVAILABLE_FONT_SIZES = Object.values(FONT_SIZES);
export const AVAILABLE_SPACINGS = Object.values(SPACINGS);

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      // Theme Mode
      isDark: true,
      
      // Colors & Effects
      accent: ACCENTS.purple,
      glassOpacity: 0.4,
      borderOpacity: 0.3,
      
      // Typography
      fontSize: FONT_SIZES.medium,
      monoFont: false,
      
      // Layout & Spacing
      spacing: SPACINGS.comfortable,
      compactMode: false,
      
      // Animation & Effects
      enableAnimations: true,
      reduceMotion: false,
      enableBlur: true,
      
      // Card Style
      cardStyle: 'glass',
      roundedCorners: 'medium',
      
      // Actions
      setTheme: (isDark) => set({ isDark }),
      setAccent: (accent) => set({ accent }),
      setGlassOpacity: (opacity) => set({ glassOpacity: opacity }),
      setBorderOpacity: (opacity) => set({ borderOpacity: opacity }),
      setFontSize: (size) => set({ fontSize: size }),
      setMonoFont: (enabled) => set({ monoFont: enabled }),
      setSpacing: (spacing) => set({ spacing: spacing }),
      setCompactMode: (enabled) => set({ compactMode: enabled }),
      setEnableAnimations: (enabled) => set({ enableAnimations: enabled }),
      setReduceMotion: (enabled) => set({ reduceMotion: enabled }),
      setEnableBlur: (enabled) => set({ enableBlur: enabled }),
      setCardStyle: (style) => set({ cardStyle: style }),
      setRoundedCorners: (radius) => set({ roundedCorners: radius }),
    }),
    {
      name: 'theme-settings',
    }
  )
);